import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useCurrentUserId } from '@/modules/service-requests/shared/employee';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-requests/shared/components/applications-table';
import {
  useBusinessCardApplications,
  useWithdrawBusinessCard,
} from '../../hooks/use-business-card';
import { BusinessCardApplication } from '../../types/business-card.types';
import { BusinessCardApplyDialog } from '../business-card-apply-dialog/business-card-apply-dialog';
import { createMyBusinessCardColumns } from '../business-card-columns/business-card-columns';

export const BusinessCardMyApplications = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const canApply = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.BUSINESS_CARD_APPLY);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.BUSINESS_CARD_WITHDRAW);
  const table = useApplicationsTable();
  const [editTarget, setEditTarget] = useState<BusinessCardApplication | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<BusinessCardApplication | null>(null);

  const { data, isLoading, error } = useBusinessCardApplications(
    {
      pageNo: table.pagination.pageIndex + 1,
      pageSize: table.pagination.pageSize,
      filter: mergeWhere({ UserId: userId }, table.columnWhere),
      order: table.order,
    },
    Boolean(userId)
  );
  const { mutateAsync: withdraw } = useWithdrawBusinessCard();

  const columns = useMemo(
    () =>
      createMyBusinessCardColumns({
        t,
        canApply,
        canWithdraw,
        onEdit: setEditTarget,
        onWithdraw: setWithdrawTarget,
      }),
    [t, canApply, canWithdraw]
  );

  return (
    <>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading}
        error={error}
        table={table}
      />
      <BusinessCardApplyDialog
        open={Boolean(editTarget)}
        onOpenChange={(open) => !open && setEditTarget(null)}
        existing={editTarget}
      />
      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_BUSINESS_CARD')}
        confirmText={t('WITHDRAW')}
        description={t('ARE_YOU_SURE_TO_WITHDRAW_BUSINESS_CARD')}
        onConfirm={() => withdrawTarget && withdraw(withdrawTarget.ItemId)}
      />
    </>
  );
};
